/**
 * ========================================
 * 주소 포맷팅 유틸리티 함수 (프론트엔드)
 * ========================================
 * Address Formatting Utility Functions (Frontend)
 * - Local Address (B07001) 포맷팅
 * - Foreign Address (B07002) 포맷팅
 * - 콤마를 포함한 주소 조합
 */

import { useSessionStore } from '@/shared/stores/sessionStore';

/**
 * 세션에서 jurisdiction 가져오기
 * @returns jurisdiction 코드 (예: 'SG') 또는 빈 문자열
 */
const getSessionJurisdiction = (): string => {
  try {
    const sessionStore = useSessionStore();
    return sessionStore.user?.customer?.jurisdiction || '';
  } catch {
    // Pinia 스토어 초기화 전에 호출되는 경우 대비
    return '';
  }
};

/**
 * 주소 정보 인터페이스
 */
export interface AddressInfo {
  ADDRESS_TYPE_CD?: string;
  addressTypeCd?: string;
  POSTAL_CODE?: string;
  postalCode?: string;
  BLOCK_NO?: string;
  blockNo?: string;
  blockHouseNo?: string;
  STREET_NAME?: string;
  streetName?: string;
  BUILDING_NAME?: string;
  buildingName?: string;
  buildingEstateName?: string;
  ADDR_LEVEL?: string;
  addrLevel?: string;
  level?: string;
  ADDR_UNIT?: string;
  addrUnit?: string;
  unit?: string;
  FOREIGN_ADDRESS_LINE_1?: string;
  foreignAddressLine1?: string;
  FOREIGN_ADDRESS_LINE_2?: string;
  foreignAddressLine2?: string;
  [key: string]: any; // 추가 속성 허용
}

/**
 * 주소 포맷팅 옵션
 */
export interface AddressFormatOptions {
  /** 콤마 사용 여부 (기본값: true) */
  useComma?: boolean;
  /** 여러 줄로 반환할지 여부 (기본값: false) */
  multiline?: boolean;
  /** 관할권 (jurisdiction이 'SG'일 때만 "SINGAPORE" 텍스트 추가) */
  jurisdiction?: string;
}

/**
 * Local Address 포맷팅 함수
 * @param addressInfo - 주소 정보 객체
 * @param options - 포맷팅 옵션
 * @returns 포맷팅된 주소 문자열 또는 문자열 배열
 * 
 * @example
 * formatLocalAddress({
 *   BLOCK_NO: '9',
 *   STREET_NAME: 'Straits View',
 *   ADDR_LEVEL: '05',
 *   ADDR_UNIT: '07',
 *   BUILDING_NAME: 'Marina One',
 *   POSTAL_CODE: '018937'
 * }, { useComma: true })
 * // "9 Straits View, #05-07, Marina One, SINGAPORE 018937"
 */
export const formatLocalAddress = (
  addressInfo: AddressInfo,
  options: AddressFormatOptions = {}
): string | string[] => {
  const { useComma = true, multiline = false } = options;
  // 세션에서 jurisdiction 가져오기 (옵션으로 전달된 값보다 세션 값 우선)
  const jurisdiction = options.jurisdiction || getSessionJurisdiction();

  // 필드 값 추출 (대소문자 구분 없이)
  const blockNo = addressInfo?.BLOCK_NO || addressInfo?.blockNo || addressInfo?.blockHouseNo || '';
  const streetName = addressInfo?.STREET_NAME || addressInfo?.streetName || '';
  const level = addressInfo?.ADDR_LEVEL || addressInfo?.addrLevel || addressInfo?.level || '';
  const unit = addressInfo?.ADDR_UNIT || addressInfo?.addrUnit || addressInfo?.unit || '';
  const buildingName = addressInfo?.BUILDING_NAME || addressInfo?.buildingName || addressInfo?.buildingEstateName || '';
  const postalCode = addressInfo?.POSTAL_CODE || addressInfo?.postalCode || '';

  const addressParts: string[] = [];

  // 1. Block/House No + Street Name
  if (blockNo && streetName) {
    addressParts.push(`${blockNo} ${streetName}`);
  } else if (blockNo) {
    addressParts.push(blockNo);
  } else if (streetName) {
    addressParts.push(streetName);
  }

  // 2. Level-Unit (#level-unit 형식)
  if (level && unit) {
    addressParts.push(`#${level}-${unit}`);
  } else if (level) {
    addressParts.push(`#${level}`);
  } else if (unit) {
    addressParts.push(`#${unit}`);
  }

  // 3. Building Name
  if (buildingName) {
    addressParts.push(buildingName);
  }

  // 4. Postal Code (jurisdiction이 'SG'일 때만 "SINGAPORE" 텍스트 추가)
  if (postalCode) {
    if (jurisdiction === 'SG') {
      addressParts.push(`SINGAPORE ${postalCode}`);
    } else {
      addressParts.push(postalCode);
    }
  }

  // 빈 문자열 제거
  const filteredParts = addressParts.filter(part => part.trim().length > 0);

  if (multiline) {
    // 여러 줄로 반환
    return filteredParts;
  }

  // 한 줄로 반환
  if (useComma) {
    // 콤마로 구분 (이미지 형식: "9 Straits View, #05-07, Marina One, SINGAPORE 018937")
    return filteredParts.join(', ').trim();
  } else {
    // 공백으로 구분
    return filteredParts.join(' ').trim();
  }
};

/**
 * Malaysia Address 포맷팅 함수
 * @param addressInfo - 주소 정보 객체 (addressLine1~3, state, city, postcode 필드 사용)
 * @param options - 포맷팅 옵션
 * @returns 포맷팅된 주소 문자열 또는 문자열 배열
 *
 * @example
 * formatMalaysiaAddress({
 *   addressLine1: 'No. 10, Jalan Sultan Ismail',
 *   addressLine2: 'Menara Keck Seng',
 *   postcode: '50250',
 *   city: 'KUALA LUMPUR',
 *   state: 'Wilayah Persekutuan Kuala Lumpur',
 * }, { useComma: true })
 * // "No. 10, Jalan Sultan Ismail, Menara Keck Seng, 50250 KUALA LUMPUR, Wilayah Persekutuan Kuala Lumpur, Malaysia"
 */
export const formatMalaysiaAddress = (
  addressInfo: AddressInfo,
  options: AddressFormatOptions = {}
): string | string[] => {
  const { useComma = true, multiline = false } = options;

  const line1 = addressInfo?.addressLine1 || '';
  const line2 = addressInfo?.addressLine2 || '';
  const line3 = addressInfo?.addressLine3 || '';
  const postcode = addressInfo?.postcode || addressInfo?.postalCode || addressInfo?.POSTAL_CODE || '';
  const city = addressInfo?.city || '';
  const state = addressInfo?.state || '';

  const parts: string[] = [];
  if (line1.trim()) parts.push(line1.trim());
  if (line2.trim()) parts.push(line2.trim());
  if (line3.trim()) parts.push(line3.trim());

  if (postcode && city) {
    parts.push(`${postcode} ${city}`);
  } else if (city) {
    parts.push(city);
  } else if (postcode) {
    parts.push(postcode);
  }

  if (state) parts.push(state);
  if (parts.length > 0) parts.push('MALAYSIA');

  const filteredParts = parts.filter(p => p.trim().length > 0);

  if (multiline) return filteredParts;
  if (filteredParts.length === 0) return '';
  return useComma ? filteredParts.join(', ').trim() : filteredParts.join(' ').trim();
};

/**
 * Foreign Address 포맷팅 함수
 * @param addressInfo - 주소 정보 객체
 * @param options - 포맷팅 옵션
 * @returns 포맷팅된 주소 문자열 또는 문자열 배열
 * 
 * @example
 * formatForeignAddress({
 *   FOREIGN_ADDRESS_LINE_1: '123 Main Street',
 *   FOREIGN_ADDRESS_LINE_2: 'New York NY 10001'
 * }, { useComma: true })
 * // "123 Main Street, New York NY 10001"
 */
export const formatForeignAddress = (
  addressInfo: AddressInfo,
  options: AddressFormatOptions = {}
): string | string[] => {
  const { useComma = true, multiline = false } = options;

  const line1 = addressInfo?.FOREIGN_ADDRESS_LINE_1 || addressInfo?.foreignAddressLine1 || '';
  const line2 = addressInfo?.FOREIGN_ADDRESS_LINE_2 || addressInfo?.foreignAddressLine2 || '';

  const parts: string[] = [];
  if (line1) parts.push(line1);
  if (line2) parts.push(line2);

  const filteredParts = parts.filter(part => part.trim().length > 0);

  if (multiline) {
    return filteredParts;
  }

  if (filteredParts.length === 0) {
    return '';
  }

  if (useComma) {
    return filteredParts.join(', ').trim();
  } else {
    return filteredParts.join(' ').trim();
  }
};

/**
 * 주소 포맷팅 함수 (자동으로 Local/Foreign 구분)
 * @param addressInfo - 주소 정보 객체
 * @param options - 포맷팅 옵션
 * @returns 포맷팅된 주소 문자열 또는 문자열 배열
 * 
 * @example
 * formatAddress({
 *   ADDRESS_TYPE_CD: 'B07001',
 *   BLOCK_NO: '9',
 *   STREET_NAME: 'Straits View',
 *   ADDR_LEVEL: '05',
 *   ADDR_UNIT: '07',
 *   BUILDING_NAME: 'Marina One',
 *   POSTAL_CODE: '018937'
 * })
 * // "9 Straits View, #05-07, Marina One, SINGAPORE 018937"
 */
export const formatAddress = (
  addressInfo: AddressInfo | null | undefined,
  options: AddressFormatOptions = {}
): string | string[] => {
  if (!addressInfo) {
    return '';
  }

  const addressTypeCd = addressInfo.ADDRESS_TYPE_CD || addressInfo.addressTypeCd || '';

  if (addressTypeCd === 'B07001') {
    // Local Address
    return formatLocalAddress(addressInfo, options);
  } else if (addressTypeCd === 'B07002') {
    // Foreign Address
    return formatForeignAddress(addressInfo, options);
  }

  // ADDRESS_TYPE_CD가 없거나 알 수 없는 경우, 필드 존재 여부로 판단
  const hasLocalFields = addressInfo.POSTAL_CODE || addressInfo.postalCode || 
                         addressInfo.BLOCK_NO || addressInfo.blockNo || addressInfo.blockHouseNo ||
                         addressInfo.STREET_NAME || addressInfo.streetName;
  const hasForeignFields = addressInfo.FOREIGN_ADDRESS_LINE_1 || addressInfo.foreignAddressLine1 ||
                           addressInfo.FOREIGN_ADDRESS_LINE_2 || addressInfo.foreignAddressLine2;

  if (hasLocalFields && !hasForeignFields) {
    return formatLocalAddress(addressInfo, options);
  } else if (hasForeignFields) {
    return formatForeignAddress(addressInfo, options);
  }

  return '';
};

/**
 * 주소를 여러 줄로 포맷팅하는 함수 (3줄 제한)
 * @param addressInfo - 주소 정보 객체
 * @param maxLines - 최대 줄 수 (기본값: 3)
 * @param options - 포맷팅 옵션 (jurisdiction 포함)
 * @returns 주소 문자열 배열 (최대 maxLines개)
 * 
 * @example
 * formatAddressLines({
 *   ADDRESS_TYPE_CD: 'B07001',
 *   BLOCK_NO: '9',
 *   STREET_NAME: 'Straits View',
 *   ADDR_LEVEL: '05',
 *   ADDR_UNIT: '07',
 *   BUILDING_NAME: 'Marina One',
 *   POSTAL_CODE: '018937'
 * }, 3, { jurisdiction: 'SG' })
 * // ["9 Straits View, #05-07", "Marina One", "SINGAPORE 018937"]
 */
export const formatAddressLines = (
  addressInfo: AddressInfo | null | undefined,
  maxLines: number = 3,
  options: AddressFormatOptions = {}
): string[] => {
  if (!addressInfo) {
    return [];
  }

  const addressTypeCd = addressInfo.ADDRESS_TYPE_CD || addressInfo.addressTypeCd || '';

  if (addressTypeCd === 'B07002') {
    // Foreign Address - 3줄로 나누기
    const line1 = addressInfo.FOREIGN_ADDRESS_LINE_1 || addressInfo.foreignAddressLine1 || '';
    const line2 = addressInfo.FOREIGN_ADDRESS_LINE_2 || addressInfo.foreignAddressLine2 || '';

    if (!line1 && !line2) {
      return [];
    }

    // 전체 텍스트 합치기
    const fullText = [line1, line2].filter(s => s.trim()).join(' ');

    if (!fullText) return [];

    // 약 30-35자 단위로 나누기 (공백 기준)
    const words = fullText.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const testLine = currentLine ? `${currentLine} ${word}` : word;

      // 각 줄은 약 30-35자 정도로 제한
      if (testLine.length > 35 && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }

      // maxLines 줄이 되면 나머지는 마지막 줄에
      if (lines.length === maxLines - 1) {
        if (i < words.length - 1) {
          currentLine = [currentLine, ...words.slice(i + 1)].join(' ');
        }
        break;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    // 최대 maxLines 줄로 제한하고 필터링
    return lines.slice(0, maxLines).filter(line => line.trim().length > 0);
  } else {
    // Local Address - 논리적으로 나누기
    // 세션에서 jurisdiction 가져오기 (옵션으로 전달된 값보다 세션 값 우선)
    const jurisdiction = options.jurisdiction || getSessionJurisdiction();
    const blockNo = addressInfo?.BLOCK_NO || addressInfo?.blockNo || addressInfo?.blockHouseNo || '';
    const streetName = addressInfo?.STREET_NAME || addressInfo?.streetName || '';
    const level = addressInfo?.ADDR_LEVEL || addressInfo?.addrLevel || addressInfo?.level || '';
    const unit = addressInfo?.ADDR_UNIT || addressInfo?.addrUnit || addressInfo?.unit || '';
    const buildingName = addressInfo?.BUILDING_NAME || addressInfo?.buildingName || addressInfo?.buildingEstateName || '';
    const postalCode = addressInfo?.POSTAL_CODE || addressInfo?.postalCode || '';

    const lines: string[] = [];

    // 첫 번째 줄: Block/House No + Street Name + Level-Unit
    let line1 = '';
    if (blockNo && streetName) {
      line1 = `${blockNo} ${streetName}`;
    } else if (blockNo) {
      line1 = blockNo;
    } else if (streetName) {
      line1 = streetName;
    }

    if (level && unit) {
      if (line1) {
        line1 += `, #${level}-${unit}`;
      } else {
        line1 = `#${level}-${unit}`;
      }
    } else if (level) {
      if (line1) {
        line1 += `, #${level}`;
      } else {
        line1 = `#${level}`;
      }
    } else if (unit) {
      if (line1) {
        line1 += `, #${unit}`;
      } else {
        line1 = `#${unit}`;
      }
    }

    if (line1) lines.push(line1);

    // 두 번째 줄: Building Name
    if (buildingName) {
      lines.push(buildingName);
    }

    // 세 번째 줄: Postal Code (jurisdiction이 'SG'일 때만 "SINGAPORE" 텍스트 추가)
    if (postalCode) {
      if (jurisdiction === 'SG') {
        lines.push(`SINGAPORE ${postalCode}`);
      } else {
        lines.push(postalCode);
      }
    }

    return lines.slice(0, maxLines).filter(line => line.trim().length > 0);
  }
};

/**
 * Vue Composable: 주소 포맷팅 함수들
 * @returns 주소 포맷팅 함수들
 * 
 * @example
 * const { formatAddress, formatLocalAddress, formatForeignAddress, formatAddressLines } = useAddressFormatter();
 * 
 * const address = formatAddress({
 *   ADDRESS_TYPE_CD: 'B07001',
 *   BLOCK_NO: '9',
 *   STREET_NAME: 'Straits View',
 *   ADDR_LEVEL: '05',
 *   ADDR_UNIT: '07',
 *   BUILDING_NAME: 'Marina One',
 *   POSTAL_CODE: '018937'
 * });
 * // "9 Straits View, #05-07, Marina One, SINGAPORE 018937"
 */
export const useAddressFormatter = () => {
  return {
    formatAddress,
    formatLocalAddress,
    formatForeignAddress,
    formatMalaysiaAddress,
    formatAddressLines
  };
};
